import {useId,useState} from 'react';
import {TaxIdElement,useCheckoutElements} from '@stripe/react-stripe-js/checkout';
import type {StripeTaxIdElementOptions,StripeCheckoutTaxIdInfo} from '@stripe/stripe-js';
import {ErrorNotice} from './components';
import {errorDetails} from './lib/store';
import type {ErrorDetails} from './lib/types';
import './checkout-business-details.css';

export type ReadyCheckout=Extract<ReturnType<typeof useCheckoutElements>,{type:'success'}>['checkout'];
const taxOptions={fields:{businessName:'never'},validation:{businessName:{required:'never'},taxId:{required:'never'}}} satisfies StripeTaxIdElementOptions;

export function useCheckoutBusinessDetails(checkout:ReadyCheckout){
  const id=useId();
  const [businessName,setBusinessName]=useState(checkout.taxIdInfo?.businessName??checkout.businessName??'');
  const [pending,setPending]=useState(false),[error,setError]=useState<ErrorDetails|null>(null);
  const hasCompany=businessName.trim().length>0;
  const clearTaxInfo=async()=>{
    setPending(true);setError(null);
    try{
      const result=await checkout.updateTaxIdInfo(null);
      if(result.type==='error')throw new Error(result.error.message,{cause:result.error});
    }catch(cause){setError(errorDetails(cause));}
    finally{setPending(false);}
  };
  return {
    blocked:pending||error!==null,
    prepare:async()=>{
      const element=checkout.getTaxIdElement();
      const value=hasCompany&&element?await element.getValue():null;
      if(value&&!value.complete)throw new Error('Check your business tax details.');
      // Stripe's live Tax ID Element accepts taxId:null for a company without a tax ID.
      // The installed Checkout type omits that case and some element-supported tax ID types.
      const details=hasCompany?{businessName:businessName.trim(),taxId:value?.value.taxId.trim()?{
        type:value.value.externalTaxIdType,value:value.value.taxId.trim()
      }:null}:null;
      const result=await checkout.updateTaxIdInfo(details as StripeCheckoutTaxIdInfo|null);
      if(result.type==='error')throw new Error(result.error.message,{cause:result.error});
      return result.session;
    },
    fields:<div className="checkout-business-details">
      <label htmlFor={`${id}-company`}>Business name</label>
      <input id={`${id}-company`} autoComplete="organization" placeholder="Company (optional)" value={businessName} disabled={pending}
        onChange={event=>{const next=event.target.value;setBusinessName(next);if(!next.trim()&&hasCompany)void clearTaxInfo();}}/>
      {hasCompany?<TaxIdElement options={taxOptions}/>:<fieldset className="checkout-tax-fields" disabled aria-describedby={`${id}-tax-help`}>
        <label>Tax ID type<select defaultValue=""><option value="">Select a tax ID type</option></select></label>
        <label>VAT or tax ID<input type="text" placeholder="Optional"/></label>
      </fieldset>}
      {!hasCompany&&<p id={`${id}-tax-help`} className="billing-note">Enter a business name to add a tax ID.</p>}
      <ErrorNotice error={error}/>
      {error&&<button type="button" className="text-button" disabled={pending} onClick={()=>void clearTaxInfo()}>Clear business tax details</button>}
    </div>
  };
}
